import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Compass, KeyRound, ChevronUp, Layers } from 'lucide-react';
import { SPECIALTY_LINKS, MARKET_LINKS, INTL_CITY_HEADLINES, mapKeywordToPage, keywordHeadline } from '../data/seoLinks';
import { COUNTRY_MARKETS } from '../data/countryMarkets';
import { GLOBAL_CONTINENTS } from '../data/globalCountries';
import { DISCIPLINE_KEYWORDS } from '../data/disciplineKeywords';
import { Link as RLink } from 'react-router-dom';

const Col = ({ title, children }) => (
  <div className="min-w-0">
    <div className="text-[11px] uppercase tracking-[0.2em] font-black text-[#0e1116] mb-4">{title}</div>
    <div className="space-y-4">{children}</div>
  </div>
);

const SeoLink = ({ to, label, headline }) => (
  <div>
    <Link to={to} className="text-[13px] font-bold text-gray-800 hover:text-green-700 transition-colors">{label}</Link>
    <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">{headline}</p>
  </div>
);

const CollapseBar = ({ icon: Ic, title, sub, open, onToggle, testId }) => (
  <button onClick={onToggle} data-testid={testId} className="w-full flex items-center justify-between gap-4 py-5 text-left group">
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-full border border-gray-300 text-green-700 flex items-center justify-center shrink-0"><Ic className="w-4 h-4" /></div>
      <div>
        <div className="text-[13px] font-black uppercase tracking-[0.15em] text-[#0e1116] group-hover:text-green-700">{title}</div>
        <p className="text-[12px] text-gray-500 mt-0.5">{sub}</p>
      </div>
    </div>
    <ChevronUp className={`w-4 h-4 text-green-700 transition-transform duration-300 ${open ? '' : 'rotate-180'}`} />
  </button>
);

// Content stays in the DOM when collapsed (height/opacity animation) so Google crawls every link.
const Collapse = ({ open, children }) => (
  <div className={`overflow-hidden transition-all duration-500 ${open ? 'max-h-[6000px] opacity-100' : 'max-h-0 opacity-0'}`} aria-hidden={!open}>
    {children}
  </div>
);

const COUNTRY_TABS = [
  { code: 'usa', label: 'United States' },
  { code: 'canada', label: 'Canada' },
  { code: 'uk', label: 'United Kingdom' },
  { code: 'australia', label: 'Australia' },
  ...GLOBAL_CONTINENTS.map(c => ({ code: `cont-${c.id}`, label: c.name, continent: c }))
];

export default function FooterSeoBand() {
  const [openExplore, setOpenExplore] = useState(false);
  const [openKw, setOpenKw] = useState(false);
  const [openDisc, setOpenDisc] = useState(false);
  const [tab, setTab] = useState('usa');
  const [discTab, setDiscTab] = useState(DISCIPLINE_KEYWORDS[0].id);

  return (
    <section className="bg-gray-50 border-t border-gray-200 px-6" data-testid="footer-seo-band">
      <div className="max-w-[1400px] mx-auto divide-y divide-gray-200">
        {/* Explore all services & locations */}
        <div>
          <CollapseBar icon={Compass} title="Explore All Services & Locations" testId="footer-explore-toggle"
            sub="Specialty survey CAD services, global markets and city pages across 40+ countries we serve."
            open={openExplore} onToggle={() => setOpenExplore(o => !o)} />
          <Collapse open={openExplore}>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-x-8 gap-y-6 pb-8">
              <Col title="Specialty Services">
                {SPECIALTY_LINKS.map(l => <SeoLink key={l.to} to={l.to} label={l.label} headline={l.headline} />)}
              </Col>
              <Col title="Global Markets">
                {MARKET_LINKS.map(l => <SeoLink key={l.to} to={l.to} label={l.label} headline={l.headline} />)}
              </Col>
              {['canada', 'uk', 'australia'].map(cc => (
                <Col key={cc} title={`${cc === 'uk' ? 'UK' : cc.charAt(0).toUpperCase() + cc.slice(1)} Cities`}>
                  {COUNTRY_MARKETS[cc].cities.map(c => (
                    <SeoLink key={c.slug} to={`/${cc}/${c.slug}`} label={`Survey Drafting ${c.name}`}
                      headline={(INTL_CITY_HEADLINES[cc] || '').replace('{city}', c.name)} />
                  ))}
                </Col>
              ))}
            </div>
            <div className="pb-8">
              <div className="text-[11px] uppercase tracking-[0.2em] font-black text-[#0e1116] mb-3">All Countries We Serve</div>
              <div className="flex flex-wrap gap-2" data-testid="footer-all-countries">
                {GLOBAL_CONTINENTS.flatMap(ct => ct.countries).map(c => (
                  <RLink key={c.slug} to={c.mainMarket || `/global/${c.slug}`}
                    className="text-[11px] bg-white border border-gray-200 text-gray-600 px-2.5 py-1 rounded-full hover:border-green-600 hover:text-green-700 transition-colors">
                    {c.name}
                  </RLink>
                ))}
              </div>
            </div>
          </Collapse>
        </div>
        {/* All keywords */}
        <div>
          <CollapseBar icon={KeyRound} title="All Keywords" testId="footer-keywords-toggle"
            sub="Every search term we target across the USA, Canada, UK, Australia and 35+ more countries, linked to its page."
            open={openKw} onToggle={() => setOpenKw(o => !o)} />
          <Collapse open={openKw}>
            <div className="pb-8">
              <div className="flex flex-wrap gap-2 mb-6">
                {COUNTRY_TABS.map(t => (
                  <button key={t.code} onClick={() => setTab(t.code)} data-testid={`kw-tab-${t.code}`}
                    className={`text-[11px] font-black uppercase tracking-wider px-4 py-2 rounded border transition-colors ${tab === t.code ? 'bg-green-700 text-white border-green-700' : 'bg-white text-gray-600 border-gray-300 hover:border-green-600'}`}>
                    {t.label}
                  </button>
                ))}
              </div>
              {COUNTRY_TABS.map(t => {
                if (t.continent) {
                  return (
                    <div key={t.code} className={tab === t.code ? 'space-y-6' : 'hidden'} data-testid={`kw-panel-${t.code}`}>
                      {t.continent.countries.map(c => (
                        <div key={c.slug}>
                          <RLink to={c.mainMarket || `/global/${c.slug}`} className="text-[13px] font-black text-[#0e1116] hover:text-green-700">{c.name} →</RLink>
                          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-2 mt-2">
                            {[...c.primaryKeywords.slice(0, 4), ...c.serviceKeywords.slice(0, 4)].map(kw => (
                              <RLink key={kw} to={c.mainMarket || `/global/${c.slug}`} className="text-[11px] text-gray-600 hover:text-green-700">{kw}</RLink>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                }
                const m = COUNTRY_MARKETS[t.code];
                const kws = [...new Set([...(m.primaryKeywords || []), ...(m.serviceKeywords || [])])];
                return (
                  <div key={t.code} className={tab === t.code ? 'grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-5' : 'hidden'} data-testid={`kw-panel-${t.code}`}>
                    {kws.map(kw => (
                      <SeoLink key={kw} to={mapKeywordToPage(kw, t.code)} label={kw.charAt(0).toUpperCase() + kw.slice(1)} headline={keywordHeadline(kw)} />
                    ))}
                  </div>
                );
              })}
            </div>
          </Collapse>
        </div>
        {/* Service discipline keywords */}
        <div>
          <CollapseBar icon={Layers} title="Service Keywords by Discipline" testId="footer-disciplines-toggle"
            sub="BIM & Digital, Building Services, Construction, Manufacturing, Industrial and Environmental — every sub-service search term, linked to its page."
            open={openDisc} onToggle={() => setOpenDisc(o => !o)} />
          <Collapse open={openDisc}>
            <div className="pb-8">
              <div className="flex flex-wrap gap-2 mb-6">
                {DISCIPLINE_KEYWORDS.map(d => (
                  <button key={d.id} onClick={() => setDiscTab(d.id)} data-testid={`disc-tab-${d.id}`}
                    className={`text-[11px] font-black uppercase tracking-wider px-4 py-2 rounded border transition-colors ${discTab === d.id ? 'bg-green-700 text-white border-green-700' : 'bg-white text-gray-600 border-gray-300 hover:border-green-600'}`}>
                    {d.name}
                  </button>
                ))}
              </div>
              {DISCIPLINE_KEYWORDS.map(d => (
                <div key={d.id} className={discTab === d.id ? 'space-y-6' : 'hidden'} data-testid={`disc-panel-${d.id}`}>
                  {d.services.map(sv => (
                    <div key={sv.slug}>
                      <RLink to={`/services/${sv.slug}`} className="text-[13px] font-black text-[#0e1116] hover:text-green-700">{sv.title} →</RLink>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-2 mt-2">
                        {sv.keywords.map(kw => (
                          <RLink key={kw} to={`/services/${sv.slug}`} className="text-[11px] text-gray-600 hover:text-green-700">{kw}</RLink>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </Collapse>
        </div>
      </div>
    </section>
  );
}
